export const TRAVEL_SERVICE_TYPES = [
  { type: "visa_processing", label: "Visa Processing" },
  { type: "flight_booking", label: "Round-trip airfare" },
  { type: "hotel_booking", label: "Hotel Accommodation" },
  { type: "airport_transfer", label: "Airport pick-up and drop off" },
  { type: "sourcing_assistance", label: "On-ground sourcing assistance" },
] as const;

export type TravelServiceType = (typeof TRAVEL_SERVICE_TYPES)[number]["type"];

const serviceLabelMap = new Map(
  TRAVEL_SERVICE_TYPES.map((s) => [s.type, s.label]),
);

export function formatServiceLabel(serviceType: string): string {
  return serviceLabelMap.get(serviceType as TravelServiceType) ?? serviceType;
}

const customServiceToTypeMap = new Map<string, TravelServiceType>(
  TRAVEL_SERVICE_TYPES.map((s) => [s.label, s.type]),
);

export function customServiceLabelToType(
  label: string,
): TravelServiceType | null {
  return customServiceToTypeMap.get(label) ?? null;
}

const BOOKING_TIMELINE = [
  { id: "details_received", label: "Details received" },
  { id: "invoice_sent", label: "Invoice sent" },
  { id: "payment_received", label: "Payment received" },
  { id: "booking_started", label: "Booking started" },
  { id: "booking_confirmed", label: "Booking confirmed" },
];

const ARRANGEMENT_TIMELINE = [
  { id: "details_received", label: "Details received" },
  { id: "arranged", label: "Arranged" },
];

export const SERVICE_TIMELINES: Record<
  TravelServiceType,
  { id: string; label: string }[]
> = {
  visa_processing: [
    { id: "details_received", label: "Details received" },
    { id: "processing_started", label: "Processing started" },
    { id: "visa_approved", label: "Visa approved" },
  ],
  flight_booking: BOOKING_TIMELINE,
  hotel_booking: BOOKING_TIMELINE,
  airport_transfer: ARRANGEMENT_TIMELINE,
  sourcing_assistance: ARRANGEMENT_TIMELINE,
};

/**
 * Defines the display order of services in the tracking timeline.
 * Also serves as the set of services included in standard/premium packages.
 */
export const STANDARD_PACKAGE_SERVICES: TravelServiceType[] = [
  "visa_processing",
  "flight_booking",
  "hotel_booking",
  "airport_transfer",
  "sourcing_assistance",
];

export function formatTravelEventLabel(
  serviceType: string,
  eventId: string,
): string {
  const timeline = SERVICE_TIMELINES[serviceType as TravelServiceType];
  const entry = timeline?.find((e) => e.id === eventId);
  return entry != null ? entry.label : eventId;
}

export function filterAndSortServices<
  T extends { serviceType: string },
>(services: T[]): T[] {
  return [...services]
    .filter((s) => s.serviceType in SERVICE_TIMELINES)
    .sort((a, b) => {
      const orderA = STANDARD_PACKAGE_SERVICES.indexOf(
        a.serviceType as TravelServiceType,
      );
      const orderB = STANDARD_PACKAGE_SERVICES.indexOf(
        b.serviceType as TravelServiceType,
      );
      return (
        (orderA === -1 ? Infinity : orderA) -
        (orderB === -1 ? Infinity : orderB)
      );
    });
}
